// Minimal Facebook JS SDK loader for WhatsApp Embedded Signup. Loaded lazily (only when the CRM is
// configured for the official WhatsApp/WABA flow) so the Baileys pilot never pulls in Meta's SDK.
// See docs/plans/whatsapp-official-api-onboarding.md.

type FbAuthResponse = { code?: string } | null;
type FbLoginResponse = { authResponse: FbAuthResponse; status: string };
type FbLoginOptions = {
  config_id: string;
  response_type: "code";
  override_default_response_type: boolean;
  extras?: Record<string, unknown>;
};

export type FacebookSdk = {
  init(params: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }): void;
  login(callback: (response: FbLoginResponse) => void, options: FbLoginOptions): void;
};

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
let loadPromise: Promise<FacebookSdk> | null = null;

/** Load + initialize the FB SDK once, memoized. Resolves with the global `FB` object. */
export function loadFacebookSdk(appId: string, version: string): Promise<FacebookSdk> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<FacebookSdk>((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, version, cookie: true, xfbml: false });
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error("Facebook SDK loaded but FB global is missing"));
        return;
      }
      window.FB.init({ appId, version, cookie: true, xfbml: false });
      resolve(window.FB);
    };

    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      loadPromise = null; // allow a retry after a transient network failure
      reject(new Error("Failed to load the Facebook SDK"));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}
